import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { CategoryPagePaths } from "@faire/retailer-visitor-shared/paths/CategoryPage";
import { useRetailerRoutingContext } from "@faire/retailer-visitor-shared/RetailerRoutingContextProvider";
import { trackEventOnClick } from "@faire/web--source/common/trackEvent";
import { Button } from "@faire/web--source/slate/Button";
import { Arrow } from "@faire/web--source/slate/icons/foundation/Arrow";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { Link } from "@faire/web--source/ui/LinkAnchor";
import { trackRetailersPillWithHighlightLowMinHover } from "@faire/web-api--source/events/retailers/hover/pillWithHighlightLowMin";
import { trackRetailersFilterPillTileView } from "@faire/web-api--source/events/retailers/view/filterPillTile";
import * as React from "react";
import { useMemo } from "react";
import styled from "styled-components";
export var FilterPillTile = function(param) {
    var brandValueFilterSection = param.brandValueFilterSection, currentCategoryModel = param.currentCategoryModel, onClick = param.onClick, pillLinkUrl = param.pillLinkUrl;
    var isMobile = useViewport().isMobile;
    var routingContext = useRetailerRoutingContext();
    var renderFilterPill = function(filterOption, index) {
        if (!filterOption.key) {
            return null;
        }
        var linkURL = pillLinkUrl ? pillLinkUrl(filterOption) : "";
        var brandValueFilterOption = filterOption.key;
        if (currentCategoryModel) {
            linkURL = CategoryPagePaths.index({
                categoryUrlIds: currentCategoryModel.urlIds,
                filterByBrandValueOption: brandValueFilterOption,
                routingContext: routingContext
            });
        }
        var trackClick = onClick;
        return /*#__PURE__*/ _jsx("div", {
            "data-test-id": "filter-pill-tile-pill-".concat(filterOption.key),
            children: /*#__PURE__*/ _jsx(PillWrapper, {
                tabIndex: 0,
                to: linkURL,
                onClick: trackClick ? function() {
                    return trackClick(brandValueFilterOption);
                } : trackEventOnClick("logged-out-category-page/pill-tile/tile-click/".concat(linkURL)),
                onMouseOver: function() {
                    return trackRetailersPillWithHighlightLowMinHover(brandValueFilterOption, "false");
                },
                children: /*#__PURE__*/ _jsx(Typography, {
                    maxLines: 2,
                    children: filterOption.display_name
                })
            })
        }, index);
    };
    var renderSeeAllLink = function() {
        if (!isFiltered) {
            return null;
        }
        var linkURL = "";
        if (currentCategoryModel) {
            linkURL = CategoryPagePaths.index({
                categoryUrlIds: currentCategoryModel.urlIds,
                routingContext: routingContext
            });
        }
        return /*#__PURE__*/ _jsxs(SeeAllRow, {
            "data-test-id": "filter-pill-tile-see-all",
            children: [
                /*#__PURE__*/ _jsx(Button, {
                    variant: "tertiary",
                    linkTo: linkURL,
                    children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                        id: "zfKfkS",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "See All Brands"
                            }
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(Spacer, {
                    size: 4
                }),
                /*#__PURE__*/ _jsx(Arrow, {
                    direction: "right"
                })
            ]
        }, "see-all");
    };
    var isFiltered = useMemo(function() {
        var _brandValueFilterSection_options;
        return !!((_brandValueFilterSection_options = brandValueFilterSection === null || brandValueFilterSection === void 0 ? void 0 : brandValueFilterSection.options) !== null && _brandValueFilterSection_options !== void 0 ? _brandValueFilterSection_options : []).find(function(option) {
            return option.is_selected;
        });
    }, [
        brandValueFilterSection === null || brandValueFilterSection === void 0 ? void 0 : brandValueFilterSection.options
    ]);
    var filterOptionsToDisplay = useMemo(function() {
        var numFilters = 6;
        var _brandValueFilterSection_options_filter_slice;
        return (_brandValueFilterSection_options_filter_slice = brandValueFilterSection === null || brandValueFilterSection === void 0 ? void 0 : brandValueFilterSection.options.filter(function(option) {
            return !option.is_selected;
        }).slice(0, isFiltered ? numFilters - 1 : numFilters)) !== null && _brandValueFilterSection_options_filter_slice !== void 0 ? _brandValueFilterSection_options_filter_slice : [];
    }, [
        isFiltered,
        brandValueFilterSection === null || brandValueFilterSection === void 0 ? void 0 : brandValueFilterSection.options
    ]);
    var viewRef = useDoActionWhenVisible(function() {
        trackRetailersFilterPillTileView();
    });
    if (!brandValueFilterSection || !filterOptionsToDisplay.length) {
        return null;
    }
    var items = filterOptionsToDisplay.map(renderFilterPill);
    var seeAll = renderSeeAllLink();
    if (seeAll && !isMobile) {
        items.push(seeAll);
    }
    return /*#__PURE__*/ _jsxs(Wrapper, {
        ref: viewRef,
        children: [
            /*#__PURE__*/ _jsx(StyledHeader, {
                "data-test-id": "filter-pill-tile-header",
                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "pFXuVZ",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Shop by brand values"
                        }
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                size: "2x"
            }),
            /*#__PURE__*/ _jsx(Column, {
                gap: "1x",
                children: items.map(function(chunk, column) {
                    return /*#__PURE__*/ _jsx("div", {
                        children: chunk
                    }, column);
                })
            })
        ]
    });
};
var Wrapper = styled(Column).withConfig({
    componentId: "sc-2f981946-0"
})([
    "padding:",
    ";",
    "{padding-left:0;> h4{padding-left:",
    ";}}"
], getSpacing("1x", 0, "1x", "2x"), responsiveMediaQueries.mobile, getSpacing("1x"));
var PillWrapper = styled(Link).withConfig({
    componentId: "sc-2f981946-1"
})([
    "display:flex;padding:",
    ";border-radius:100px;border:1px solid ",
    ";width:fit-content;max-width:100%;cursor:pointer;&:hover,&:focus,&:active{color:",
    ";background-color:",
    ";border-color:",
    ";> p{color:",
    ";}}> p{word-break:break-word;}white-space:nowrap;"
], getSpacing(9, "2x"), Core.border.muted, Core.text.inverse, Core.surface.hover, Core.border.hover, Core.text.inverse);
var SeeAllRow = styled(Row).attrs({
    align: "center"
}).withConfig({
    componentId: "sc-2f981946-2"
})([
    "padding:",
    ";",
    "{padding-top:",
    ";}"
], getSpacing(18, "1x"), responsiveMediaQueries.mobile, getSpacing(18));
var StyledHeader = styled(Typography).attrs({
    forwardedAs: "h4",
    variant: "pageHeaderSerifRegular"
}).withConfig({
    componentId: "sc-2f981946-3"
})([
    "&&&{font-weight:300;}"
]);
